import React, { useState, useEffect } from 'react';
import { Select, Spin, Button, message, Space } from 'antd';
import { SyncOutlined, TeamOutlined } from '@ant-design/icons';
import { getWechatGroups, syncWechatGroups, WechatGroup } from '../services/wechatGroup';
import { checkWechatGroupAvailability } from '../services/case';

interface WechatGroupSelectProps {
  value?: number;
  onChange?: (value: number | undefined, groupName?: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  showSync?: boolean;
  // 新增：用于排除当前案件ID（编辑模式）
  excludeCaseId?: number;
  // 新增：验证失败回调
  onValidationFailed?: (error: string) => void;
}

const WechatGroupSelect: React.FC<WechatGroupSelectProps> = ({
  value,
  onChange,
  placeholder = '选择企业微信群聊',
  allowClear = true,
  disabled = false,
  showSync = true,
  excludeCaseId,
  onValidationFailed,
}) => {
  const [groups, setGroups] = useState<WechatGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const fetchGroups = async (keyword?: string) => {
    setLoading(true);
    try {
      const response = await getWechatGroups({
        page: 1,
        page_size: 100,
        status: 'active',
        keyword,
      });
      setGroups(response.items || []);
    } catch (error) {
      console.error('Failed to fetch WeChat groups:', error);
      message.error('获取群聊列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncWechatGroups();
      if (result.success) {
        message.success(result.message);
        fetchGroups(searchKeyword);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('Failed to sync WeChat groups:', error);
      message.error('同步群聊失败');
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    if (value) {
      fetchGroups(value);
    }
  };

  const handleChange = async (selectedValue: number | undefined) => {
    // 如果选择了群，进行即时验证
    if (selectedValue && onValidationFailed) {
      try {
        const validation = await checkWechatGroupAvailability(selectedValue, excludeCaseId);

        if (!validation.available) {
          // 验证失败，显示错误提示并清空选择
          onValidationFailed(validation.message || '该企业微信群已关联其他案件');
          if (onChange) {
            onChange(undefined);
          }
          return;
        }
      } catch (error) {
        console.error('Failed to validate wechat group:', error);
        // 验证失败时也清空选择
        onValidationFailed('验证企业微信群失败，请稍后重试');
        if (onChange) {
          onChange(undefined);
        }
        return;
      }
    }

    // 验证通过或清空选择，调用原有回调
    if (onChange) {
      const selectedGroup = groups.find(g => g.id === selectedValue);
      onChange(selectedValue, selectedGroup?.group_name);
    }
  };

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        loading={loading}
        showSearch
        filterOption={false}
        onSearch={handleSearch}
        style={{ flex: 1 }}
        notFoundContent={loading ? <Spin size="small" /> : '暂无群聊'}
      >
        {groups.map((group) => (
          <Select.Option key={group.id} value={group.id}>
            <Space>
              <TeamOutlined />
              <span>{group.group_name}</span>
              {group.member_count && (
                <span style={{ color: '#999', fontSize: '12px' }}>
                  ({group.member_count}人)
                </span>
              )}
            </Space>
          </Select.Option>
        ))}
      </Select>
      {showSync && (
        <Button
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSync}
          loading={syncing}
          disabled={disabled}
          title="同步企业微信群聊"
        >
          同步
        </Button>
      )}
    </Space.Compact>
  );
};

export default WechatGroupSelect;
